import React from 'react';
import {
  Box,
  Stack,
  Link,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  FormHelperText,
  useTheme,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';

import { ReactComponent as GoogleLogo } from 'src/assets/google-logo.svg';
import { CustomButton } from 'src/components/customButton/CustomButton';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
interface IProps {
  email: string;
  isConsentChecked: boolean;
  onEmailChange: (email: string) => void;
  onConsentChange: (checked: boolean) => void;
  onSubmit: () => void;
  onOtherOptions: () => void;
  onOpenConsentTerms: () => void;
  isLoading?: boolean;
  emailError?: string;
  consentError?: string;
  emailTouched?: boolean;
  consentTouched?: boolean;
  isGoogleOAuthUser?: boolean;
  hideOtherOptions?: boolean;
}

export const EmailFormFields: React.FC<IProps> = ({
  email,
  isConsentChecked,
  onEmailChange,
  onConsentChange,
  onSubmit,
  onOtherOptions,
  onOpenConsentTerms,
  isLoading = false,
  emailError,
  consentError,
  emailTouched,
  consentTouched,
  isGoogleOAuthUser = false,
  hideOtherOptions = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <TextField
        name="email"
        placeholder="Your email"
        label="Email"
        type="email"
        variant="outlined"
        autoFocus={!isGoogleOAuthUser}
        autoComplete="email"
        onChange={(e) => onEmailChange(e.target.value)}
        value={email}
        error={emailTouched && Boolean(emailError)}
        helperText={emailTouched && emailError}
        disabled={isGoogleOAuthUser}
        InputProps={{
          readOnly: isGoogleOAuthUser,
          endAdornment: isGoogleOAuthUser ? (
            <>
              <SvgIcon
                inheritViewBox
                component={LockOutlinedIcon}
                color="secondary"
                sx={{
                  width: '20px',
                  height: '20px',
                  mr: 0.5,
                }}
              />
              <SvgIcon
                inheritViewBox
                component={GoogleLogo}
                sx={{
                  width: '20px',
                  height: '20px',
                }}
              />
            </>
          ) : undefined,
        }}
        sx={(theme) => ({
          width: '400px',
          marginBottom: '8px',
          [theme.breakpoints.down('md')]: {
            width: '100%',
          },
          ...(isGoogleOAuthUser && {
            '& .MuiInputBase-input': {
              cursor: 'default',
            },
            '& .Mui-disabled': {
              color: theme.palette.text.primary,
              WebkitTextFillColor: theme.palette.text.primary,
            },
          }),
        })}
      />

      <Box mb={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isConsentChecked}
              onChange={(e) => onConsentChange(e.target.checked)}
              name="isConsentChecked"
              disabled={isGoogleOAuthUser}
            />
          }
          label={
            <Typography
              variant="body1"
              onClick={(e) => e.preventDefault()}
              sx={(theme) => ({
                [theme.breakpoints.up('lg')]: {
                  whiteSpace: 'nowrap',
                },
              })}
            >
              I agree to{' '}
              <Link onClick={onOpenConsentTerms} sx={{ cursor: 'pointer' }}>
                the personal data processing terms
              </Link>
            </Typography>
          }
          sx={{ mb: 0.5 }}
        />
        {consentTouched && consentError && (
          <FormHelperText
            sx={(theme) => ({
              color: theme.palette.error.main,
              marginLeft: '2rem',
            })}
          >
            {consentError}
          </FormHelperText>
        )}

        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          mb={2}
          mt={2}
          spacing={isMobile ? 2 : 3}
        >
          <CustomButton
            fullWidth={isMobile}
            isLoading={isLoading}
            onClick={onSubmit}
            label={isGoogleOAuthUser ? 'Continue' : 'Continue with email'}
            testid="continue-button"
            color="primary"
            style={{
              width: isMobile ? '100%' : '228px',
              whiteSpace: 'nowrap',
              minWidth: '0',
            }}
          />

          {!isGoogleOAuthUser && !hideOtherOptions && (
            <Box
              display="flex"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={onOtherOptions}
              role="button"
              tabIndex={0}
            >
              <SvgIcon
                component={ArrowBackRoundedIcon}
                fontSize="small"
                sx={{
                  color: '#FFB74D',
                  cursor: 'pointer',
                  mr: 0.5,
                }}
                role="button"
              />
              <Typography variant="body1" onClick={(e) => e.preventDefault()}>
                <Link sx={{ cursor: 'pointer' }}>Other options </Link>
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
